import React, { useMemo } from "react";
import {
  Search, Plus, Phone, Mail, FileText, CreditCard, ShoppingCart, CheckSquare,
  LifeBuoy, DollarSign, Wallet, Repeat, PieChart, CheckCircle2, AlertCircle,
  AlertTriangle, Edit2, Trash2, Smile, Meh, Frown, Timer, Moon, AtSign, Smartphone,
  Users as UsersIcon, Globe, StickyNote
} from "lucide-react";
import { CATEGORIES } from "../lib/constants";
import NotificationsBell from "./NotificationsBell";

const ICONS = {
  Phone, Mail, FileText, CreditCard, ShoppingCart, CheckSquare,
  LifeBuoy, DollarSign, Wallet, Repeat,
};

const CatIcon = ({ name, size = 18, className = "" }) => {
  const Cmp = ICONS[name] || CheckSquare;
  return <Cmp size={size} strokeWidth={1.75} className={className} />;
};

export default function Dashboard({
  tasks, postIts, activeTab, setActiveTab, searchTerm, setSearchTerm,
  openModal, deleteTask, toggleComplete, setView, isIdle, totalSeconds, dailyQuote,
}) {
  const isOverdue = (dateStr, isCompleted) => {
    if (isCompleted || !dateStr) return false;
    const today = new Date().toISOString().split("T")[0];
    return dateStr < today;
  };

  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((t) => t.completed).length;
    const overdue = tasks.filter((t) => isOverdue(t.dueDate, t.completed)).length;
    const pending = total - completed;

    const pendingCredits = tasks
      .filter((t) => t.category === "CREDITI" && !t.completed)
      .reduce((acc, t) => acc + (Number(t.amount || 0) - Number(t.paidAmount || 0)), 0);
    const pendingAcconti = tasks
      .filter((t) => t.category === "ACCONTI" && !t.completed)
      .reduce((acc, t) => acc + (Number(t.amount || 0) - Number(t.paidAmount || 0)), 0);

    return { total, completed, overdue, pending, pendingCredits, pendingAcconti };
  }, [tasks]);

  const formatTotalTime = (totalSecs) => {
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const getOverdueDays = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return Math.ceil((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return -999;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getRecallStatus = (task) => {
    if (task.category !== "RECALL" || !task.entryDate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const entry = new Date(task.entryDate);
    const due = task.dueDate ? new Date(task.dueDate) : null;
    const daysFromEntry = Math.floor((today.getTime() - entry.getTime()) / (1000 * 60 * 60 * 24));
    const daysFromDue = due ? Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)) : -999;
    if (daysFromDue >= 60) return { text: "Definire persa", color: "bg-red-800 text-white border-red-900" };
    if (daysFromDue >= 30) return { text: "Sollecitare con promozione", color: "bg-orange-100 text-orange-800 border-orange-300" };
    if (daysFromEntry >= 15) return { text: "Ricontattare il cliente", color: "bg-yellow-100 text-yellow-800 border-yellow-300" };
    return null;
  };

  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    if (activeTab !== "DASHBOARD") filtered = filtered.filter((t) => t.category === activeTab);
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter((t) =>
        (t.client || "").toLowerCase().includes(s) ||
        (t.description || "").toLowerCase().includes(s) ||
        (t.phone || "").includes(s) ||
        (t.email || "").toLowerCase().includes(s) ||
        (t.invoiceNumber || "").toLowerCase().includes(s) ||
        (t.proformaNumber || "").toLowerCase().includes(s)
      );
    }
    return filtered.sort((a, b) => {
      if (a.completed === b.completed) {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      }
      return a.completed ? 1 : -1;
    });
  }, [tasks, activeTab, searchTerm]);

  const renderTaskList = (taskList, title, badgeText, badgeClass) => (
    <div className="bg-white p-5 rounded-xl border border-slate-200 flex flex-col h-[620px]" data-testid={`task-list-${title.replace(/\s/g, "-")}`}>
      <h3 className="text-sm font-bold font-outfit text-slate-900 mb-3 flex items-center justify-between">
        <span>{title}</span>
        {badgeText && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeClass}`}>
            {badgeText}
          </span>
        )}
      </h3>
      <div className="overflow-y-auto flex-1 pr-2 custom-scrollbar space-y-2">
        {taskList.length === 0 && (
          <div className="text-center py-10 text-slate-400 text-sm">Nessuna attività</div>
        )}
        {taskList.map((task) => {
          const overdueDays = getOverdueDays(task.dueDate);
          const daysUntil = getDaysUntilDue(task.dueDate);
          const isOverdueTask = overdueDays > 0;
          const isSevere = overdueDays > 3;
          const isApproaching = !isOverdueTask && daysUntil >= 0 && daysUntil <= 2;

          return (
            <div
              key={task.id}
              onClick={() => openModal(task)}
              className={`task-row flex items-center p-3 rounded-lg border-l-4 cursor-pointer ${
                isSevere ? "bg-red-50 border-red-600 pulse-urgent"
                  : isOverdueTask ? "bg-red-50 border-red-400"
                  : isApproaching ? "bg-amber-50 border-amber-400"
                  : "bg-slate-50 border-slate-300"
              }`}
              data-testid={`task-item-${task.id}`}
            >
              <div className={`p-2 rounded-full mr-3 bg-white shadow-sm shrink-0 text-${CATEGORIES[task.category]?.accent || 'slate'}-600`}>
                <CatIcon name={CATEGORIES[task.category]?.icon} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {task.client}
                  {isSevere && <span className="ml-1 text-red-600 font-black">!</span>}
                </div>
                <div className={`text-xs mt-1 ${isOverdueTask ? "text-red-600" : isApproaching ? "text-amber-700" : "text-slate-500"}`}>
                  {task.dueDate ? new Date(task.dueDate).toLocaleDateString("it-IT") : "Nessuna scadenza"}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); toggleComplete(task.id); }}
                className="ml-2 text-slate-300 hover:text-emerald-500 transition p-2 hover:bg-emerald-50 rounded-full shrink-0"
                title="Segna come fatto"
                data-testid={`task-complete-btn-${task.id}`}
              >
                <CheckCircle2 size={22} strokeWidth={1.75} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-6 shrink-0">
        <h2 className="text-2xl font-outfit font-bold tracking-tight text-slate-900" data-testid="page-title">
          {activeTab === "DASHBOARD" ? "Panoramica Attività" : CATEGORIES[activeTab]?.label}
        </h2>
        <div className="flex items-center space-x-2">
          <NotificationsBell />
          <div className="relative hidden md:block">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cerca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-full bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-64 transition-all text-sm"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => openModal()}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-full flex items-center gap-2 font-semibold text-sm transition-transform hover:scale-105 shadow-md"
            data-testid="new-task-btn"
          >
            <Plus size={18} strokeWidth={2.2} />
            <span className="hidden sm:inline">Nuova</span>
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-6 bg-slate-50 custom-scrollbar">
        {activeTab === "DASHBOARD" ? (
          <div className="space-y-6 h-full flex flex-col">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 shrink-0">
              <KpiCard label="Totale" value={stats.total} color="blue" Icon={CheckSquare} testId="kpi-total" />
              <KpiCard label="Scadute" value={stats.overdue} color="red" Icon={AlertCircle} testId="kpi-overdue" valueColor="text-red-600" />
              <KpiCard label="Crediti" value={`€ ${stats.pendingCredits.toLocaleString("it-IT")}`} color="emerald" Icon={DollarSign} testId="kpi-credits" valueColor="text-emerald-600" small />
              <KpiCard label="Acconti" value={`€ ${stats.pendingAcconti.toLocaleString("it-IT")}`} color="cyan" Icon={Wallet} testId="kpi-acconti" valueColor="text-cyan-600" small />
              <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between" data-testid="kpi-percent">
                <div>
                  <p className="text-slate-500 text-[11px] font-bold uppercase tracking-wider">% Fatto</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-2xl font-outfit font-bold text-slate-900">
                      {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                    </p>
                    {(() => {
                      const percent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
                      if (percent > 70) return <Smile size={22} className="text-emerald-500" />;
                      if (percent > 50) return <Meh size={22} className="text-amber-500" />;
                      return <Frown size={22} className="text-red-500" />;
                    })()}
                  </div>
                </div>
                <div className="bg-teal-100 p-2 rounded-full text-teal-600"><PieChart size={20} /></div>
              </div>
            </div>

            {/* Layout: Left column (post-it preview + timer) + Right lists */}
            <div className="flex-1 flex flex-col xl:flex-row gap-6 overflow-hidden min-h-0">
              <div className="w-full xl:w-1/3 flex flex-col gap-4 h-full overflow-hidden">
                <PostItsPreview postIts={postIts} setView={setView} />

                <div
                  className={`bg-gradient-to-br from-ink to-slate-800 rounded-xl p-4 text-white flex flex-col items-center text-center shrink-0 transition-all ${
                    !isIdle ? "timer-active" : "opacity-70 grayscale"
                  }`}
                  data-testid="activity-timer"
                >
                  <div className="flex items-center gap-2 text-amber-200 mb-1">
                    {isIdle ? <Moon size={15} /> : <Timer size={15} />}
                    <span className="uppercase tracking-wider text-[10px] font-bold">
                      {isIdle ? "In Pausa" : "Tempo Totale"}
                    </span>
                  </div>
                  <div className="text-2xl font-outfit font-bold font-mono mb-2">{formatTotalTime(totalSeconds)}</div>
                  <div className="border-t border-white/20 w-full pt-2">
                    <p className="text-xs italic font-medium text-amber-100/90">&ldquo;{dailyQuote}&rdquo;</p>
                  </div>
                </div>
              </div>

              <div className="w-full xl:w-2/3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full min-h-0">
                <div className="h-full overflow-hidden">
                  {renderTaskList(
                    filteredTasks.filter((t) => !t.completed && t.category !== "CREDITI" && t.category !== "ACCONTI").slice(0, 50),
                    "Attività Operative",
                    "Priorità Alta",
                    "bg-red-100 text-red-700"
                  )}
                </div>
                <div className="h-full overflow-hidden">
                  {renderTaskList(
                    filteredTasks.filter((t) => !t.completed && t.category === "CREDITI").slice(0, 50),
                    "Crediti",
                    "Da incassare",
                    "bg-emerald-100 text-emerald-700"
                  )}
                </div>
                <div className="h-full overflow-hidden">
                  {renderTaskList(
                    filteredTasks.filter((t) => !t.completed && t.category === "ACCONTI").slice(0, 50),
                    "Acconti",
                    "Da chiudere",
                    "bg-cyan-100 text-cyan-700"
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <CategoryView
            filteredTasks={filteredTasks}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            openModal={openModal}
            deleteTask={deleteTask}
            toggleComplete={toggleComplete}
            getOverdueDays={getOverdueDays}
            getRecallStatus={getRecallStatus}
          />
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, Icon, testId, valueColor, small = false }) {
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between" data-testid={testId}>
      <div className="min-w-0">
        <p className="text-slate-500 text-[11px] font-bold uppercase tracking-wider">{label}</p>
        <p className={`${small ? "text-xl" : "text-2xl"} font-outfit font-bold ${valueColor || "text-slate-900"} truncate`}>{value}</p>
      </div>
      <div className={`bg-${color}-100 p-2 rounded-full text-${color}-600 shrink-0`}>
        <Icon size={20} />
      </div>
    </div>
  );
}

function CategoryView({ filteredTasks, searchTerm, setSearchTerm, openModal, deleteTask, toggleComplete, getOverdueDays, getRecallStatus }) {
  return (
    <div className="space-y-4">
      <div className="md:hidden relative">
        <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Cerca..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredTasks.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">
            Nessuna attività
          </div>
        )}
        {filteredTasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            openModal={openModal}
            deleteTask={deleteTask}
            toggleComplete={toggleComplete}
            getOverdueDays={getOverdueDays}
            getRecallStatus={getRecallStatus}
          />
        ))}
      </div>
    </div>
  );
}

function TaskCard({ task, openModal, deleteTask, toggleComplete, getOverdueDays, getRecallStatus }) {
  const overdueDays = getOverdueDays(task.dueDate);
  const isOverdueTask = overdueDays > 0;
  const isSevere = !task.completed && overdueDays > 3;
  const recallStatus = getRecallStatus(task);
  const cat = CATEGORIES[task.category];

  return (
    <div
      onClick={() => openModal(task)}
      className={`task-row bg-white rounded-xl p-5 border cursor-pointer transition-all ${
        task.completed ? "opacity-60 border-slate-200"
          : isSevere ? "border-red-600 ring-4 ring-red-100 bg-red-50"
          : isOverdueTask ? "border-red-300 bg-red-50/40"
          : "border-slate-200"
      }`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`px-2 py-1 text-[10px] font-semibold rounded-md uppercase tracking-wide ${cat?.color}`}>
            {cat?.label}
          </span>
          {task.visibility === "public" && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 flex items-center gap-1" title="Pubblico">
              <Globe size={10} /> Pubblico
            </span>
          )}
          {task.visibility === "shared" && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 flex items-center gap-1" title={`Condiviso con ${task.shared_with?.length || 0} utenti`}>
              <UsersIcon size={10} /> Condiviso
            </span>
          )}
          {task._is_owner === false && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" title="Non sei il proprietario di questo task">
              Da altro utente
            </span>
          )}
        </div>
        <div className="flex space-x-1">
          <button
            onClick={(e) => { e.stopPropagation(); openModal(task); }}
            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition"
            data-testid={`edit-task-${task.id}`}
          >
            <Edit2 size={15} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition"
            data-testid={`delete-task-${task.id}`}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      <div className="flex items-center mb-2 gap-2 flex-wrap">
        {isSevere && <AlertTriangle size={18} className="text-red-600 animate-pulse" />}
        <h3 className={`font-outfit font-bold text-lg text-slate-900 ${task.completed ? "line-through" : ""}`}>
          {task.client}
        </h3>
        {recallStatus && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold border ${recallStatus.color} animate-pulse`}>
            {recallStatus.text}
          </span>
        )}
      </div>

      {task.description && (
        <p className="text-slate-600 text-sm mb-3 line-clamp-2">{task.description}</p>
      )}

      {(task.phone || task.email) && (
        <div className="mb-3 space-y-1 pt-2 border-t border-dashed border-slate-200">
          {task.phone && (
            <div className="flex items-center text-sm text-slate-600">
              <Smartphone size={13} className="mr-2 text-slate-400" />
              <span className="font-mono">{task.phone}</span>
            </div>
          )}
          {task.email && (
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-slate-600 truncate">
                <AtSign size={13} className="mr-2 text-slate-400" />
                <span className="truncate">{task.email}</span>
              </div>
              <a
                href={`mailto:${task.email}?subject=${encodeURIComponent(task.description || "Nuova Attività")}`}
                onClick={(e) => e.stopPropagation()}
                className="ml-2 px-2.5 py-1 bg-blue-50 text-blue-700 text-[11px] font-bold rounded-md hover:bg-blue-100 border border-blue-200 flex items-center shrink-0"
                title="Scrivi mail"
              >
                <Mail size={11} className="mr-1" /> Invia
              </a>
            </div>
          )}
        </div>
      )}

      {(task.category === "CREDITI" || task.category === "ACCONTI") && (
        <FinanceBlock task={task} accent={task.category === "CREDITI" ? "emerald" : "cyan"} />
      )}
      {task.category === "ORDINARE" && (task.orderNumber || task.orderDate) && (
        <DocBlock label="Comm." num={task.orderNumber} date={task.orderDate} bg="orange" />
      )}
      {task.category === "PAGARE" && (task.invoiceNumber || task.invoiceDate) && (
        <DocBlock label="Doc." num={task.invoiceNumber} date={task.invoiceDate} bg="red" />
      )}
      {task.category === "RECALL" && (task.offerNumber || task.offerDate) && (
        <DocBlock label="Prev." num={task.offerNumber} date={task.offerDate} bg="pink" />
      )}

      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div>
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">Scadenza</span>
          <div className={`text-sm font-semibold ${
            isSevere ? "text-red-700 font-bold" : isOverdueTask && !task.completed ? "text-red-600" : "text-slate-800"
          }`}>
            {task.dueDate ? new Date(task.dueDate).toLocaleDateString("it-IT") : "N/D"}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); toggleComplete(task.id); }}
          className={`flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            task.completed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
          data-testid={`toggle-complete-${task.id}`}
        >
          {task.completed ? (
            <><CheckCircle2 size={14} className="mr-1" /> Fatto</>
          ) : (
            <><div className="w-3 h-3 border-2 border-slate-400 rounded-full mr-1" /> Da fare</>
          )}
        </button>
      </div>
    </div>
  );
}

function FinanceBlock({ task, accent }) {
  const amount = Number(task.amount || 0);
  const paid = Number(task.paidAmount || 0);
  const balance = amount - paid;
  const percent = amount > 0 ? Math.min(100, Math.round((paid / amount) * 100)) : 0;

  return (
    <div className={`mb-3 bg-${accent}-50 p-3 rounded-lg border border-${accent}-100`}>
      {(task.invoiceNumber || task.invoiceDate || task.proformaNumber) && (
        <div className="flex flex-col text-[11px] text-slate-500 mb-2 font-medium">
          {(task.invoiceNumber || task.invoiceDate) && (
            <div className="flex items-center gap-1">
              <FileText size={11} />
              {task.invoiceNumber && <span>Fatt. {task.invoiceNumber}</span>}
              {task.invoiceDate && <span>del {new Date(task.invoiceDate).toLocaleDateString("it-IT")}</span>}
            </div>
          )}
          {task.proformaNumber && (
            <div className="flex items-center gap-1">
              <FileText size={11} />
              <span>Prof. {task.proformaNumber}</span>
            </div>
          )}
        </div>
      )}
      <div className="flex justify-between text-xs mb-1 text-slate-500">
        <span>Incassato: <span className="font-semibold text-slate-800">€{paid.toLocaleString("it-IT")}</span></span>
        <span>Totale: <span className="font-semibold text-slate-800">€{amount.toLocaleString("it-IT")}</span></span>
      </div>
      <div className="flex justify-between items-center pt-1 border-t border-slate-200 mt-1">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Saldo:</span>
        <span className={`text-lg font-outfit font-black text-${accent === "emerald" ? "red" : "cyan"}-600`}>
          €{balance.toLocaleString("it-IT")}
        </span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2">
        <div className={`h-1.5 rounded-full bg-${accent}-500`} style={{ width: `${percent}%` }}></div>
      </div>
    </div>
  );
}

function DocBlock({ label, num, date, bg }) {
  return (
    <div className={`mb-3 bg-${bg}-50 p-2.5 rounded-lg border border-${bg}-100`}>
      <div className="flex items-center gap-1 text-[11px] text-slate-600 font-medium">
        <FileText size={11} />
        {num && <span className="mr-1">{label} {num}</span>}
        {date && <span>del {new Date(date).toLocaleDateString("it-IT")}</span>}
      </div>
    </div>
  );
}

function PostItsPreview({ postIts, setView }) {
  // Palette di 8 colori distinti per le miniature
  const POSTIT_HUES = [
    "linear-gradient(135deg,#fef9c3 0%,#fde047 100%)",  // yellow classico
    "linear-gradient(135deg,#fed7aa 0%,#fb923c 100%)",  // arancio
    "linear-gradient(135deg,#fbcfe8 0%,#f472b6 100%)",  // rosa
    "linear-gradient(135deg,#bbf7d0 0%,#4ade80 100%)",  // verde
    "linear-gradient(135deg,#bae6fd 0%,#38bdf8 100%)",  // celeste
    "linear-gradient(135deg,#ddd6fe 0%,#a78bfa 100%)",  // viola
    "linear-gradient(135deg,#fecaca 0%,#f87171 100%)",  // rosso tenue
    "linear-gradient(135deg,#e2e8f0 0%,#94a3b8 100%)",  // grigio
  ];
  // 4 anteprime più grandi (2x2) per leggere il testo
  const items = (postIts || []).slice(0, 4);
  return (
    <div className="bg-white rounded-xl border border-slate-200 flex-1 min-h-0 overflow-hidden flex flex-col" data-testid="postits-preview">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote size={15} className="text-amber-600" />
          <h3 className="font-outfit font-bold text-sm text-slate-900">
            Anteprima Note Rapide
          </h3>
          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
            {postIts.length}
          </span>
        </div>
        <button
          onClick={() => setView("POSTITS")}
          className="text-[10px] tracking-[0.15em] uppercase text-gold hover:text-ink transition-colors"
          data-testid="postits-see-all-btn"
        >
          Vedi tutte →
        </button>
      </div>

      <div className="p-3 flex-1 overflow-y-auto custom-scrollbar">
        {items.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-xs italic">
            Nessuna nota. Vai su Post-it per aggiungerne.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5 h-full auto-rows-fr">
            {items.map((note, idx) => (
              <button
                key={note.id}
                type="button"
                onClick={() => setView("POSTITS")}
                className="postit-preview group relative rounded-md shadow-md text-left overflow-hidden transition-all duration-200 hover:scale-[1.05] hover:z-10 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-gold min-h-[110px]"
                style={{ background: POSTIT_HUES[idx % POSTIT_HUES.length] }}
                data-testid={`postit-mini-${idx}`}
                title={note.text || "(vuota)"}
              >
                <span className="absolute top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-amber-700/60 shadow z-10" />
                <span
                  className="block px-3 pt-4 pb-2 text-[13px] leading-tight font-handwriting text-slate-900/90 overflow-hidden line-clamp-6 whitespace-pre-line"
                >
                  {note.text || "(vuota)"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

